import { motion } from "framer-motion";
import { Radio, HeartHandshake, ArrowUpRight } from "lucide-react";

const NODES = [
  {
    icon: Radio,
    overline: "Media · Events · Culture",
    name: "USDD Networks",
    desc: "The wider media, events and cultural ecosystem powering everything we do — from broadcasts to brand collaborations.",
    cta: "Explore USDD Networks",
    href: "https://usddnetworks.co.uk",
    accent: "yellow",
  },
  {
    icon: HeartHandshake,
    overline: "Community · Social Impact",
    name: "Soul Collective CIC",
    desc: "Our community and social-impact arm — accessible programmes, outreach and grassroots initiatives across the UK.",
    cta: "Coming Soon",
    href: null, // disabled until a dedicated page exists
    accent: "purple",
    comingSoon: true,
  },
];

export const Ecosystem = () => {
  return (
    <section
      id="ecosystem"
      data-testid="ecosystem-section"
      className="relative py-14 sm:py-16 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-950/60 to-black" />
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex items-center gap-3 mb-3">
          <span className="h-px w-8 bg-zinc-600" />
          <span className="text-[10px] font-bold uppercase tracking-[0.35em] text-zinc-400">The Ecosystem</span>
        </div>
        <h2 className="font-display text-3xl sm:text-4xl text-white tracking-wide max-w-3xl">
          USDD Academy is part of a wider <span className="text-brand-yellow">creative ecosystem</span>.
        </h2>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          {NODES.map((n, i) => {
            const isLink = !!n.href;
            const Tag = isLink ? motion.a : motion.div;
            const linkProps = isLink
              ? {
                  href: n.href,
                  target: n.href.startsWith("http") ? "_blank" : undefined,
                  rel: n.href.startsWith("http") ? "noreferrer" : undefined,
                }
              : { "aria-disabled": "true" };
            return (
              <Tag
                key={n.name}
                {...linkProps}
                data-testid={`ecosystem-${n.name.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`group relative p-7 sm:p-8 rounded-2xl bg-white/[0.025] border border-white/10 transition-all ${
                  isLink ? "hover:border-white/25 hover:bg-white/[0.05] cursor-pointer" : "opacity-80 cursor-default"
                }`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center border ${
                      n.accent === "yellow"
                        ? "bg-brand-yellow/8 border-brand-yellow/30"
                        : "bg-brand-purple/15 border-brand-purpleLight/40"
                    }`}
                  >
                    <n.icon
                      size={20}
                      className={n.accent === "yellow" ? "text-brand-yellow" : "text-brand-purpleLight"}
                    />
                  </div>
                  {n.comingSoon ? (
                    <span data-testid={`ecosystem-${n.name.toLowerCase().replace(/\s+/g, "-")}-coming-soon`} className="text-[10px] uppercase tracking-[0.28em] font-black text-brand-purpleLight border border-brand-purpleLight/40 rounded-full px-3 py-1 bg-brand-purple/10">
                      Coming Soon
                    </span>
                  ) : (
                    <ArrowUpRight className="text-zinc-500 group-hover:text-white group-hover:rotate-12 transition-all" size={20} />
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">{n.overline}</div>
                <h3 className="font-display text-3xl text-white tracking-wide">{n.name}</h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed max-w-md">{n.desc}</p>
                <div
                  className={`mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] ${
                    n.accent === "yellow" ? "text-brand-yellow" : "text-brand-purpleLight"
                  } ${isLink ? "group-hover:gap-3 transition-all" : ""}`}
                >
                  {n.cta} {isLink && <ArrowUpRight size={14} />}
                </div>
              </Tag>
            );
          })}
        </div>
      </div>
    </section>
  );
};
